import React from 'react';
import { FiPieChart, FiPlus, FiTrash2 } from 'react-icons/fi';
import { generateId } from '@/utils/helpers';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  CheckboxGroup,
  CheckboxLabel,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText,
  CalculatedValue,
  CalculatedLabel,
  CalculatedAmount,
  DynamicEntryContainer,
  EntryHeader,
  EntryTitle,
  EntryNumber,
  RemoveButton,
  AddButton
} from '../common/FormStyles';
import { InformalSavings, InformalTontine } from '@/types/application-forms';
import { DocumentUpload, UploadedDocument } from '../common/DocumentUpload';
import { INFORMAL_SAVINGS_DOCUMENTS } from '@/config/documentsBySection';
import { Candidate } from '@/models';

interface InformalSavingsSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;

  uploadedDocuments?: UploadedDocument[];
       onDocumentUpload?: (type: string, file: File | null) => void;
  onDocumentRemove?: (type: string) => void;
}

export const InformalSavingsSection: React.FC<InformalSavingsSectionProps> = ({
  data,
  onChange,
  readonly = false,
  uploadedDocuments = [],
  onDocumentUpload,
  onDocumentRemove,
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleSavingsTypeToggle = (type: string, checked: boolean) => {
    const currentTypes = data.types_epargne || [];
    const newTypes = checked
      ? [...currentTypes, type as any]
      : currentTypes.filter(t => t !== type);
    handleChange('types_epargne', newTypes);
  };

  const handleTontineChange = (index: number, field: string, value: any) => {
    const newTontines = [...(data.tontines || [])];
    newTontines[index] = { ...newTontines[index], [field]: value };
    handleChange('tontines', newTontines);
  };

  const addTontine = () => {
    const newTontine: InformalTontine = {
      id: generateId(),
      montant: 0,
      frequence: 'Mensuel',
      responsable: '',
      telephone_responsable: ''
    };
    handleChange('tontines', [...(data.tontines || []), newTontine]);
  };

  const removeTontine = (index: number) => {
    const newTontines = (data.tontines || []).filter((_, i) => i !== index);
    handleChange('tontines', newTontines);
  };

  const calculateTotalEpargne = () => {
    const {
      epargne_bancaire = 0,
      epargne_mobile_money = 0,
      epargne_informelle = 0
    } = data;

    return (
      Number(epargne_bancaire || 0) +
      Number(epargne_mobile_money || 0) +
      Number(epargne_informelle || 0)
    );
  };


  const totalEpargne = calculateTotalEpargne();

  React.useEffect(() => {
    if (data.total_epargne !== totalEpargne) {
      handleChange('total_epargne', totalEpargne);
    }
  }, [totalEpargne]);

  return (
    <FormSection>
      <SectionTitle>
        <FiPieChart />
        Épargne et Tontines
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Vos habitudes d'épargne et votre participation à des tontines montrent votre
          capacité à gérer votre argent et à honorer des engagements financiers réguliers.
        </p>
      </InfoBox>

      <SubSectionTitle>Tontines</SubSectionTitle>

      <FormGroup>
        <Label>Participez-vous à des tontines ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.participe_tontines || false}
            onChange={e => handleChange('participe_tontines', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.participe_tontines ? 'Oui, je participe à des tontines' : 'Non'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.participe_tontines && (
        <>
          {(data.tontines || []).map((tontine, index) => (
            <DynamicEntryContainer key={tontine.id}>
              <EntryHeader>
                <EntryTitle>
                  <EntryNumber>{index + 1}</EntryNumber>
                  Tontine {index + 1}
                </EntryTitle>
                <RemoveButton
                  onClick={() => removeTontine(index)}
                  disabled={readonly}
                >
                  <FiTrash2 />
                  Supprimer
                </RemoveButton>
              </EntryHeader>

              <FormGrid>
                <FormGroup>
                  <Label>Montant de la cotisation (FCFA)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={tontine.montant || ''}
                    onChange={e => handleTontineChange(index, 'montant', parseInt(e.target.value) || 0)}
                    placeholder="Ex: 10000"
                    disabled={readonly}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Fréquence</Label>
                  <Select
                    value={tontine.frequence || 'Mensuel'}
                    onChange={e => handleTontineChange(index, 'frequence', e.target.value)}
                    disabled={readonly}
                  >
                    <option value="Quotidien">Quotidien</option>
                    <option value="Hebdomadaire">Hebdomadaire</option>
                    <option value="Mensuel">Mensuel</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <Label>Nom du responsable de la tontine</Label>
                <Input
                  type="text"
                  value={tontine.responsable || ''}
                  onChange={e => handleTontineChange(index, 'responsable', e.target.value)}
                  placeholder="Nom et prénoms"
                  disabled={readonly}
                />
              </FormGroup>

              <FormGroup>
                <Label>Téléphone du responsable</Label>
                <Input
                  type="tel"
                  value={tontine.telephone_responsable || ''}
                  onChange={e => handleTontineChange(index, 'telephone_responsable', e.target.value)}
                  placeholder="+221 X XX XX XX XX"
                  disabled={readonly}
                />
                <HelpText>
                  Ce numéro pourra être contacté pour vérifier votre participation
                </HelpText>
              </FormGroup>
            </DynamicEntryContainer>
          ))}

          {!readonly && (
            <AddButton onClick={addTontine}>
              <FiPlus />
              Ajouter une tontine
            </AddButton>
          )}
        </>
      )}

      <SubSectionTitle>Épargne</SubSectionTitle>

      <FormGroup>
        <Label>Comment épargnez-vous ?</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_epargne || []).includes('Tontine')}
              onChange={e => handleSavingsTypeToggle('Tontine', e.target.checked)}
              disabled={readonly}
            />
            Tontine (voir ci-dessus)
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_epargne || []).includes('Mobile Money')}
              onChange={e => handleSavingsTypeToggle('Mobile Money', e.target.checked)}
              disabled={readonly}
            />
            Mobile Money
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_epargne || []).includes('À la maison')}
              onChange={e => handleSavingsTypeToggle('À la maison', e.target.checked)}
              disabled={readonly}
            />
            À la maison
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_epargne || []).includes('Compte bancaire')}
              onChange={e => handleSavingsTypeToggle('Compte bancaire', e.target.checked)}
              disabled={readonly}
            />
            Compte bancaire
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_epargne || []).includes('Microfinance')}
              onChange={e => handleSavingsTypeToggle('Microfinance', e.target.checked)}
              disabled={readonly}
            />
            Microfinance
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.types_epargne || []).includes('Autre')}
              onChange={e => handleSavingsTypeToggle('Autre', e.target.checked)}
              disabled={readonly}
            />
            Autre
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Vous pouvez sélectionner plusieurs méthodes</HelpText>
      </FormGroup>

      {(data.types_epargne || []).includes('Mobile Money') && (
        <FormGroup>
          <Label>Montant épargné sur Mobile Money (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_mobile_money || ''}
            onChange={e => handleChange('epargne_mobile_money', parseInt(e.target.value) || 0)}
            placeholder="Ex: 100000"
            disabled={readonly}
          />
        </FormGroup>
      )}

      {(data.types_epargne || []).includes('À la maison') && (
        <FormGroup>
          <Label>Montant épargné à la maison (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_informelle || ''}
            onChange={e => handleChange('epargne_informelle', parseInt(e.target.value) || 0)}
            placeholder="Ex: 50000"
            disabled={readonly}
          />
          <HelpText>Estimation de l'argent gardé chez vous</HelpText>
        </FormGroup>
      )}

      {(data.types_epargne || []).includes('Compte bancaire') && (
        <FormGroup>
          <Label>Montant sur compte bancaire (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_bancaire || ''}
            onChange={e => handleChange('epargne_bancaire', parseInt(e.target.value) || 0)}
            placeholder="Ex: 200000"
            disabled={readonly}
          />
        </FormGroup>
      )}

      {(data.types_epargne || []).includes('Microfinance') && (
        <FormGroup>
          <Label>Montant en microfinance (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_microfinance || ''}
            onChange={e => handleChange('epargne_microfinance', parseInt(e.target.value) || 0)}
            placeholder="Ex: 150000"
            disabled={readonly}
          />
        </FormGroup>
      )}

      {(data.types_epargne || []).includes('Autre') && (
        <FormGroup>
          <Label>Autre épargne - Montant (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.epargne_informelle || ''}
            onChange={e => handleChange('epargne_informelle', parseInt(e.target.value) || 0)}
            placeholder="Montant"
            disabled={readonly}
          />
        </FormGroup>
      )}

      {totalEpargne > 0 && (
        <CalculatedValue>
          <CalculatedLabel>Total épargne disponible</CalculatedLabel>
          <CalculatedAmount>{totalEpargne.toLocaleString()} FCFA</CalculatedAmount>
        </CalculatedValue>
      )}

      <FormGroup>
        <Label>Pouvez-vous épargner chaque mois ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.peut_epargner_mensuellement || false}
            onChange={e => handleChange('peut_epargner_mensuellement', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.peut_epargner_mensuellement ? 'Oui, je peux épargner' : 'Non, pas régulièrement'}
          </ToggleLabel>
        </ToggleSwitch>
      </FormGroup>

      {data.peut_epargner_mensuellement && (
        <FormGroup>
          <Label>Montant que vous pouvez épargner par mois (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.montant_epargne_mensuelle || ''}
            onChange={e => handleChange('montant_epargne_mensuelle', parseInt(e.target.value))}
            placeholder="Ex: 20000"
            disabled={readonly}
          />
          <HelpText>
            Estimation du montant que vous pouvez mettre de côté chaque mois
          </HelpText>
        </FormGroup>
      )}

      <InfoBox $type="info">
        <p>
          <strong>Bon à savoir :</strong> Une épargne régulière, même modeste, démontre votre
          capacité à gérer vos finances et à honorer des engagements mensuels comme un loyer.
        </p>
      </InfoBox>

      <SubSectionTitle>Épargne</SubSectionTitle>
      
            <FormGrid>
              <FormGroup>
                <Label>Compte épargne bancaire (FCFA)</Label>
                <Input
                  type="number"
                  min="0"
                  value={data.epargne_bancaire || ''}
                  onChange={e => handleChange('epargne_bancaire', parseInt(e.target.value) || 0)}
                  placeholder="Montant disponible"
                  disabled={readonly}
                />
              </FormGroup>
      
              <FormGroup>
                <Label>Mobile Money épargne (FCFA)</Label>
                <Input
                  type="number"
                  min="0"
                  value={data.epargne_mobile_money || ''}
                  onChange={e => handleChange('epargne_mobile_money', parseInt(e.target.value) || 0)}
                  placeholder="Montant disponible"
                  disabled={readonly}
                />
              </FormGroup>
            </FormGrid>
            <FormGrid>
                    
            
                    <FormGroup>
                      <Label>Épargne informelle (FCFA)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={data.epargne_informelle || ''}
                        onChange={e => handleChange('epargne_informelle', parseInt(e.target.value) || 0)}
                        placeholder="Ex: à la maison"
                        disabled={readonly}
                      />
                    </FormGroup>
                  </FormGrid>

            <CalculatedValue>
                    <CalculatedLabel>Total épargne disponible</CalculatedLabel>
                    <CalculatedAmount>{totalEpargne.toLocaleString()} FCFA</CalculatedAmount>
                  </CalculatedValue>

      
       {onDocumentUpload && onDocumentRemove && (
                  <DocumentUpload
                    sectionTitle="Vos Revenus"
                    documents={INFORMAL_SAVINGS_DOCUMENTS}
                    uploadedFiles={uploadedDocuments}
                    onFileSelect={onDocumentUpload}
                    onFileRemove={onDocumentRemove}
                    readonly={readonly}
                  />
                )}
            
    </FormSection>
  );
};
